import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Card, CardActionArea, CircularProgress, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { Navigate, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAuthStore, Account } from '../../store/authStore';
import AuthService from '../../api/services/auth.service';
import type { User } from '../../types';
import AuthLayout from './AuthLayout';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [loginUser, setLoginUser] = useState<User | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<Account[]>([]);
  const hasRedirected = React.useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by the API client when a request came back 401 on a session that
  // had been signed in. Say what happened rather than presenting a blank
  // form that looks like the app forgot them for no reason.
  const expired = searchParams.get('expired') === '1';
  const { isAuthenticated, setAuth, setAccounts } = useAuthStore();

  // Redirect if already logged in (only once)
  useEffect(() => {
    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await AuthService.login({
        email: formData.email,
        password: formData.password,
      });

      console.log('Login successful:', response);

      // Check if we got accounts list (new multi-account flow)
      if (response.accounts && response.accounts.length > 0) {
        setLoginUser(response.user);
        setAvailableAccounts(response.accounts);
        setAccounts(response.accounts);

        // If only one account, auto-select it
        if (response.accounts.length === 1) {
          await handleAccountSelect(response.user._id, response.accounts[0]._id);
        } else {
          // Multiple accounts - show selection screen
          setShowAccountSelection(true);
          setLoading(false);
        }
      } else if (response.token) {
        // Legacy flow - already authenticated
        setAuth(response.user, response.token);
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      // The api client rejects with the raw AxiosError, whose .message is
      // "Request failed with status code 403" — useless to the person locked
      // out. Prefer the server's explanation, which for an unaccepted
      // invitation names the company and points at the email.
      setError(
        err.response?.data?.message || err.message || 'Login failed. Please check your credentials.'
      );
      setLoading(false);
    }
  };

  const handleAccountSelect = async (userId: string, accountId: string) => {
    setError('');
    setLoading(true);

    try {
      const response = await AuthService.selectAccount({ userId, accountId });
      
      console.log('Account selected:', response);

      // Store auth with account in Zustand store
      setAuth(response.user, response.token, response.account);

      // Navigate immediately after setting auth
      hasRedirected.current = true;
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Account selection error:', err);
      setError(err.message || 'Failed to select account.');
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={showAccountSelection ? 'Choose an account' : 'Welcome back'}
      subtitle={
        showAccountSelection
          ? 'You belong to more than one company. Pick the one to work in.'
          : 'Sign in to continue.'
      }
      footer={
        !showAccountSelection && (
          <Link component={RouterLink} to="/forgot-password" variant="body2" color="text.secondary">
            Forgot your password?
          </Link>
        )
      }
    >
      {expired && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your session ended. Sign in again to pick up where you were.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showAccountSelection ? (
        <Box>
          <Stack gap={1}>
            {availableAccounts.map((account) => (
              <Card
                key={account._id}
                sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => loginUser && handleAccountSelect(loginUser._id, account._id)}
              >
                <CardActionArea sx={{ p: 2 }}>
                  <Typography variant="subtitle1">{account.companyName || 'Company'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {account.role} · {account.status}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
          <Button
            fullWidth
            onClick={() => {
              setShowAccountSelection(false);
              setLoginUser(null);
              setAvailableAccounts([]);
            }}
            sx={{ mt: 2 }}
          >
            Back to sign in
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="current-password"
              disabled={loading}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3, py: 1.25 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

        </Box>
      )}
    </AuthLayout>
  );
};

// Signed in already: straight to the dashboard, before the form exists.
// Rendering the form even for one frame was enough for the browser to
// offer saved passwords (Touch ID on a Mac), and that prompt stayed on
// screen over the dashboard it had been skipped for.
const LoginRoute: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />;
};

export default LoginRoute;
